import '.d{display:flex;}.ssr-plugin'
import '.e{flex-direction:column;}.ssr-plugin'
import '.g{gap:1rem;}.ssr-plugin'
import '.ex{column-gap:1rem;}.ssr-plugin'
import Column from '@app/components/layout/Column'
import useMediaQuery from '@app/util/useMediaQuery'
import { Nutshell } from '@app/components/blocks/nutshell/Nutshell'
import { useLanguage, useTranslations } from '@i18n/useLanguage'
import { pages } from '@app/util/links'
import { SectionHeader } from '@app/components/blocks/sectionHeader/SectionHeader'
import { DiscussionCard } from '@app/components/blocks/discussions/DiscussionCard'
import { CreatePostDrawer } from '@app/components/blocks/discussions/CreatePostDrawer'
import { Modal } from '@app/components/blocks/modal/Modal'
import { Text } from '@app/components/core/typography/Text'
import { InitiativeCard } from '@app/components/blocks/card/InitiativeCard'
import { styled } from 'inlines'
import { navigate } from 'wouter/use-browser-location'
import { useState } from 'react'
import { useQuery, useAuthState, useClient } from '@based/sdk/react'
import {
  PostViewModel,
  ThreadLatestViewModel,
} from 'functions/app/thread/view-latest'
import { GlobalPostViewModel } from 'functions/app/thread/view-global'
import { labelAddressBuilder, fullName } from '@app/util/helpers'
import { useRelativeDateFormat } from '@app/util/useDateFormat'
import { useAuthModal } from '@app/util/hooks/useAuthModal'
import { usePostActions } from '@app/util/hooks/usePostActions'
import {
  getPostedInLabel,
  getPostShareUrl,
  getPostShareTitle,
  toGlobalPostViewModel,
} from '@app/pages/discussions/discussionsHelpers'
import {
  AppearItem,
  AppearSection,
} from '@app/components/blocks/contentSection/blocks/AppearInViewport'
import { RegionalListInitiativeModel } from 'functions/app/initiative/list-regional'
import { RegionalListPlotModel } from 'functions/app/plot/list-regional'
import defaultProjectPicture from '@app/assets/defaultProjectPicture.png'
import { SafeHtml } from '@app/components/core/safeHtml/SafeHtml'
import { ContentSection } from '@app/components/blocks/contentSection/ContentSection'
import { resolveLocalizedField } from '@i18n/localizeHelper'
import {
  CONTENT_BLOCK_TYPE,
  sortContentBlocksByOrder,
} from '@schema/pageContentBlock'
import { GeoViewModel } from './models'
import {
  regionsTabsQueryParamName,
  regionTabs,
  municipalityTabs,
} from '../../constants'

export const Overview = ({ entity }: { entity: GeoViewModel }) => {
  const [language] = useLanguage()
  const isRegion = 'municipalities' in entity
  const addressListingContext = 'regional'
  const isMobile = useMediaQuery()
  const largerGap = isMobile ? '2rem' : '3.5rem'
  const translations = useTranslations().app
  const tDiscussions = useTranslations().app.discussions
  const relativeDate = useRelativeDateFormat()
  const authState = useAuthState()
  const client = useClient()
  const { withAuthCheck } = useAuthModal()
  const { deletePost } = usePostActions()
  const discussionTabRoute = `?${regionsTabsQueryParamName}=${regionTabs.discussion}`
  const [editPost, setEditPost] = useState<GlobalPostViewModel | null>(null)
  const [confirmDeletePostId, setConfirmDeletePostId] = useState<number | null>(
    null,
  )
  const [deleteLoading, setDeleteLoading] = useState(false)

  const isAuthor = (post: PostViewModel) =>
    !!authState.userId && post.author?.id === Number(authState.userId)

  const handleLike = (postId: number) => {
    void withAuthCheck(async () => {
      await client.call('react-post', { postId })
    })
  }

  const handleDelete = async () => {
    if (confirmDeletePostId === null) {
      return
    }
    setDeleteLoading(true)
    const ok = await deletePost(confirmDeletePostId)
    if (ok) {
      setConfirmDeletePostId(null)
    }
    setDeleteLoading(false)
  }

  const listPayload = isRegion
    ? { regionId: entity.id, top: 2 }
    : { municipalityId: entity.id, top: 2 }

  const listPayloadCount = isRegion
    ? { regionId: entity.id, count: true }
    : { municipalityId: entity.id, count: true }

  const discussionsQuery = useQuery('view-latest-thread', {
    id: entity.thread?.id,
    top: 2,
  })
  const discussions: ThreadLatestViewModel = discussionsQuery.data ?? {
    posts: [],
  }

  const initiativeQuery = useQuery('list-regional-initiative', listPayload)
  const initiatives: RegionalListInitiativeModel[] = initiativeQuery.data ?? []

  const plotQuery = useQuery('list-regional-plot', listPayload)
  const plots: RegionalListPlotModel[] = plotQuery.data ?? []

  const plotCountQuery = useQuery('list-regional-plot', listPayloadCount)
  const plotsCount = plotCountQuery.data?.count ?? 0

  const serviceProviderQuery = useQuery(
    'list-regional-service-providers',
    listPayloadCount,
  )
  const serviceProvidersCount = serviceProviderQuery.data?.count ?? 0

  const textContentBlocks = sortContentBlocksByOrder(
    (entity.contentBlocks ?? []).filter(
      (block) => !block.type || block.type === CONTENT_BLOCK_TYPE.text,
    ),
  )

  return (
    <Column gap={largerGap} alignItems="stretch">
      <div className="d e g"
      >
        <SafeHtml html={entity?.content} />
        {textContentBlocks.map((block, index) => (
          <ContentSection
            key={block.id ?? index}
            section={{
              type: 'text',
              title: block.title ?? '',
              body: resolveLocalizedField(block.body, language),
              truncationThreshold: block.truncationThreshold ?? 5,
            }}
          />
        ))}
      </div>
      <Nutshell
        heading={entity.name + translations.regionPage.nutshell.inAnutshell}
        items={[
          {
            total: entity.initiatives?.count ?? 0,
            subtitle: translations.regionPage.initiatives,
          },
          ...(plotsCount > 0
            ? [
                {
                  total: plotsCount,
                  subtitle: translations.regionPage.locations,
                },
              ]
            : []),
          ...(isRegion && (entity.municipalities?.length ?? 0) > 0
            ? [
                {
                  total: entity.municipalities?.length ?? 0,
                  subtitle: translations.regionPage.municipalities,
                },
              ]
            : []),
          ...(serviceProvidersCount > 0
            ? [
                {
                  total: serviceProvidersCount,
                  subtitle: translations.regionPage.experts,
                },
              ]
            : []),
        ]}
        style={{
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
        }}
      />

      {discussions?.posts?.length > 0 && (
        <AppearSection>
          <Column alignItems="stretch" gap={0}>
            <AppearItem staggerIndex={0}>
              <SectionHeader
                title={translations.regionPage.latestDiscussions}
                seeMore={translations.common.seeMore}
                toRoute={discussionTabRoute}
              />
            </AppearItem>
            <div className="ex"
              style={{
                columnCount: isMobile ? 1 : 2,}}
            >
              {discussions.posts.map((post, index) => {
                const likedUserIds = (post.viewers ?? [])
                  .filter((v) => v.$reaction === 'like')
                  .map((v) => v.id.toString())
                const liked =
                  !!authState.userId &&
                  likedUserIds.includes(authState.userId.toString())

                return (
                  <AppearItem
                    key={post.id}
                    staggerIndex={index + 1}
                    style={{ breakInside: 'avoid', marginBottom: '1rem' }}
                  >
                    <DiscussionCard
                      authorName={
                        post.author
                          ? fullName(post.author)
                          : translations.common.unknown
                      }
                      authorAvatar={post.author?.avatar?.src}
                      postedIn={getPostedInLabel(post.threads)}
                      date={
                        post.createdAt
                          ? relativeDate(new Date(post.createdAt))
                          : ''
                      }
                      updatedAt={
                        post.updatedAt &&
                        post.updatedAt > (post.createdAt ?? 0)
                          ? relativeDate(new Date(post.updatedAt))
                          : undefined
                      }
                      title={post.title ?? undefined}
                      content={post.content ?? ''}
                      likes={likedUserIds.length}
                      liked={liked}
                      onLikeClick={() => handleLike(post.id)}
                      replies={post.replies?.length ?? 0}
                      categories={post.categories}
                      media={(post.media ?? [])
                        .filter((item) => item.status === 'ready')
                        .map((item) => ({
                          src: item.src,
                          mimeType: item.mimeType,
                          name: item.name,
                        }))}
                      shareUrl={getPostShareUrl(post.id)}
                      shareTitle={getPostShareTitle(
                        post.title ?? undefined,
                        post.content ?? undefined,
                      )}
                      onEdit={
                        isAuthor(post)
                          ? () => setEditPost(toGlobalPostViewModel(post))
                          : undefined
                      }
                      onDelete={
                        isAuthor(post)
                          ? () => setConfirmDeletePostId(post.id)
                          : undefined
                      }
                      onClick={() =>
                        navigate(`${discussionTabRoute}&post=${post.id}`)
                      }
                    />
                  </AppearItem>
                )
              })}
            </div>
          </Column>
        </AppearSection>
      )}

      {initiatives?.length > 0 && (
        <Column alignItems="stretch" gap={0}>
          <SectionHeader
            title={translations.regionPage.popularInitiatives}
            seeMore={translations.common.seeMore}
            toRoute={`?${regionsTabsQueryParamName}=${regionTabs.initiatives}`}
          />
          <div className="d g"
            style={{
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            {initiatives.map((initiative) => (
              <InitiativeCard
                key={initiative.slug}
                to={pages.initiative(initiative.slug)}
                image={initiative.images?.[0] ?? defaultProjectPicture}
                title={initiative.title}
                description={initiative.description}
                location={labelAddressBuilder(
                  initiative.location?.address,
                  translations.common.notSpecified,
                  addressListingContext,
                )}
                phase={initiative.phase?.name}
                tagText={
                  initiative.searchingForMembers
                    ? translations.common.initiativeStatus.searchingForMembers
                    : translations.common.initiativeStatus
                        .notSearchingForMembers
                }
                tagVariant={
                  initiative.searchingForMembers ? 'success' : 'neutral'
                }
              />
            ))}
          </div>
        </Column>
      )}
      {!isRegion && plots?.length > 0 && (
        <Column alignItems="stretch" gap={0}>
          <SectionHeader
            title={translations.regionPage.locations}
            seeMore={translations.common.seeMore}
            toRoute={`?${regionsTabsQueryParamName}=${municipalityTabs.locations}`}
          />
          <div className="d g"
            style={{
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            {plots.map((plot) => (
              <InitiativeCard
                key={plot.slug}
                image={plot.media?.[0] ?? defaultProjectPicture}
                title={plot.name}
                description={plot.description}
                location={labelAddressBuilder(
                  plot.location?.address,
                  translations.common.notSpecified,
                  addressListingContext,
                )}
                tagText={
                  translations.common.plotApplicationStatus[
                    plot.applicationStatus
                  ]
                }
                tagVariant={
                  plot.applicationStatus === 'applicationOpen'
                    ? 'success'
                    : 'neutral'
                }
                to={pages.plot(plot.slug)}
              />
            ))}
          </div>
        </Column>
      )}

      {/* <Column alignItems="stretch" gap={0}>
        <SectionHeader
          title={translations.regionPage.current}
          seeMore={translations.common.seeMore}
          toRoute={pages.initiatives}
        />
        <Row>
          <Column>
            <BlogCard
              image="https://images.pexels.com/photos/2917838/pexels-photo-2917838.jpeg"
              meta="Nieuws"
              title="Tinyhouses Westpark het groenste kleine wijkje van Stad"
            />
          </Column>

          <Column>
            <Event
              date={26}
              month="Augustus"
              category="INFORMATIE"
              title="Informatiebijeenkomst over CPO-bouwen in Mill"
              time="10:00 - 12:00"
              location="Amsterdam"
            />
            <Event
              date={14}
              month="Oktober"
              category="INFORMATIE"
              title="Open dag Twee Amsterdam"
              time="10:00 - 12:00"
              location="Amsterdam"
            />
            <Event
              date={14}
              month="Oktober"
              category="INFORMATIE"
              title="Informatiebijeenkomst over CPO-bouwen in Mill"
              time="10:00 - 12:00"
              location="Amsterdam"
            />
          </Column>
        </Row>
      </Column> */}

      <CreatePostDrawer
        open={!!editPost}
        editPost={editPost}
        context="thread"
        threadId={entity.thread?.id}
        onClose={() => setEditPost(null)}
      />

      <Modal
        isOpen={confirmDeletePostId !== null}
        loading={deleteLoading}
        backLabel={tDiscussions.cancel}
        confirmLabel={tDiscussions.deleteConfirm}
        onClose={() => setConfirmDeletePostId(null)}
        onConfirm={() => void handleDelete()}
      >
        <Text.Medium>{tDiscussions.deleteConfirmDescription}</Text.Medium>
      </Modal>
    </Column>
  )
}
